<script lang="ts">
	//Helpers
	import {
		get_gen_1_path_from_category,
		get_local_ip,
		get_mode,
		real_id,
	} from '../../../../../helpers/interface_helpers';
	import { formatSnakeCaseToTitle } from '../../../../../helpers/utilities';
	//Services
	import { localReq, postReq } from '../../../../../services/http';
	import { format } from '../../../../../services/i18n';
	//Stores
	import {
		getDeviceStatus,
		getDeviceSettings,
		readDeviceSettings,
	} from 'src/stores/device-runtime/status-selectors';
	import { devices_settings } from 'src/stores/device-runtime/status';
	//Design system components
	import { Radio, Checkbox, toast, Button, Tooltip } from '@Shelly/shelly-design-system';
	import { tooltipText } from '../tooltip_text';

	export let device;

	const id = real_id(device.id);

	let settings = readDeviceSettings(id);
	$: settingsStore = getDeviceSettings(id);
	$: settings = $settingsStore ?? settings;

	const btnTypes = [
		'momentary',
		'toggle',
		'edge',
		'detached',
		'action',
		'cycle',
		'momentary_on_release',
	];
	const category = get_gen_1_path_from_category(device);

	let reverse = settings[category][device.channel].btn_reverse;

	let cacheReverse = reverse;

	let loading = false;

	let index = btnTypes.indexOf(settings[category][device.channel].btn_type);
	let cacheIndex = index;
	let disabled = settings[category][device.channel].default_state === 'switch';

	let toolTipMesage = (type: string) => {
		return {
			message: 'btn_type_unavailable_switch_info_toast',
			values: { pod_type: type },
		};
	};
	let localIp = get_local_ip(id);

	const toggleReverse = () => {
		reverse = !reverse;
	};

	const setType = (val) => {
		index = val;
	};

	const sentRequests = async () => {
		let setBtnType; //prepare variable for first promise
		let handleToggle; //prepare variable for second promise
		if (cacheIndex !== index) {
			loading = true;
			if (localIp) {
				setBtnType = await localReq(
					device,
					`${localIp}/settings/${device.category}/${device.channel}`,
					'post',
					{
						btn_type: btnTypes[index],
					},
				);
			} else {
				setBtnType = await postReq(`/device/${device.category}/settings/button_type`, {
					id: id,
					channel: device.channel,
					type: btnTypes[index],
				});
			}
		}
		if (cacheReverse !== reverse) {
			loading = true;
			if (localIp) {
				handleToggle = await localReq(
					device,
					`${localIp}/settings/${device.category}/${device.channel}`,
					'post',
					{
						btn_reverse: reverse ? 1 : 0,
					},
				);
			} else {
				handleToggle = await postReq(`/device/${device.category}/settings/reverse_toggle`, {
					id: id,
					channel: device.channel,
					type: reverse ? 1 : 0,
				});
			}
		}

		Promise.all([setBtnType, handleToggle]).then((res) => {
			if (typeof res[0] !== 'undefined') {
				if (res[0].isok) {
					settings[category][device.channel].btn_type = btnTypes[index];
					devices_settings.update_devices_settings(id, settings);
					toast.open({
						type: 'success',
						title:
							$format('btn_type_scc_toast', {
								values: { type_value: formatSnakeCaseToTitle(btnTypes[index]) },
							}) ||
							`Button type was changed to ${formatSnakeCaseToTitle(btnTypes[index])}.`,
					});
					cacheIndex = index;
				} else {
					toast.open({ type: 'danger', title: $format('error_global') });
					index = cacheIndex;
				}
			}
			if (typeof res[1] !== 'undefined') {
				if (res[1].isok) {
					settings[category][device.channel].btn_reverse = reverse;
					devices_settings.update_devices_settings(id, settings);
					toast.open({
						type: 'success',
						title: $format('btn_type_reversed_scc_toast') || `Button type was reversed`,
					});
					cacheReverse = reverse;
				} else {
					toast.open({ type: 'danger', title: $format('error_global') });
					reverse = cacheReverse;
				}
			}
			loading = false;
		});
	};
</script>

<Tooltip placement="top" title={tooltipText(toolTipMesage('switch'), disabled)}>
	<Radio
		size="m"
		selected={index.toString()}
		value={'0' || '6'}
		{disabled}
		on:click={() => setType(0)}
		id="radio-0poysap"
		>{$format('option_momentary_by_category', {
			values: { device_category: $format(`this_${category}`) },
		}) ||
			'Momentary - Set this {category} to be "Momentary" switch. Push-for "ON" or Push-for "OFF".'}</Radio
	>
</Tooltip>
{#if index === 0 || index === 6}
	<Radio
		size="m"
		selected={index.toString()}
		value={'6'}
		on:click={() => setType(index === 6 ? 0 : 6)}
		id="radio-su5t6d5"
		>{$format('button_option_alert_1') ||
			"Don't activate if the button is long pushed. Notice: If short pushed, the relay will be switched only after the button is released."}</Radio
	>
{/if}
<Radio size="m" selected={index.toString()} value={'1'} on:click={() => setType(1)} id="radio-95hl4o9"
	>{$format('option_toggle_switch_by_category', {
		values: { device_category: $format(`this_${category}`) },
	}) ||
		'Toggle Switch - Set this {category} to be "Toggle" switch. Act as a flip switch with one state for "ON" and one state for "OFF".'}</Radio
>
<Tooltip placement="top" title={tooltipText(toolTipMesage('switch'), disabled)}>
	<Radio
		size="m"
		selected={index.toString()}
		value={'2'}
		{disabled}
		on:click={() => setType(2)}
		id="radio-78bsoek"
		>{$format('option_edge_switch_by_category', {
			values: { device_category: $format(`this_${category}`) },
		}) ||
			'Edge Switch - Set this {category} to be "Edge" switch. Changes state on every hit.'}</Radio
	>
</Tooltip>
<Radio size="m" selected={index.toString()} value={'3'} on:click={() => setType(3)} id="radio-d7lwg3u"
	>{$format('option_detached_by_category', {
		values: { device_category: $format(`this_${category}`) },
	}) ||
		'Detached Switch - Set this {category} to be in "Detached" switch category - switch is separated from the relays.'}</Radio
>
<Radio size="m" selected={index.toString()} value={'4'} on:click={() => setType(4)} id="radio-rgr8tva"
	>{$format('option_activation_switch') ||
		'Activation Switch - use it for motion sensor. Any input turns "ON" and resets Auto OFF timer.'}</Radio
>
<Radio size="m" selected={index.toString()} value={'5'} on:click={() => setType(5)} id="radio-cu8gezs"
	>{$format('option_cycle_switch') ||
		'Cycle Switch - At the push of a button, the sequence is: Ch1-On Ch-2-Off, Ch1-Off Ch-On, Ch1 & Ch2 On, Ch1 & Ch2 Off.'}</Radio
>

<Checkbox size="m" checked={reverse} value={1} on:click={toggleReverse} id="checkbox-mtttc2e"
	>{$format('option_reverse_inputs') ||
		'Reverse inputs - If you enable this option, the buttons On/Off position will be inverted.'}</Checkbox
>

<Button class="mt-s" {loading} size="m" width="full" on:click={sentRequests}
	>{$format('button_save')}</Button
>
